import { TextFieldProps } from "@mui/material"
import { Override } from "@tm/models"
import { FocusEventHandler, forwardRef, SyntheticEvent, useCallback } from "react"
import { NumberFormatValues, NumericFormat, NumericFormatProps, OnValueChange } from "react-number-format"
import { useLocalization } from "@tm/localization"
import { MuiTextField } from "./MuiTextField"

export type NumberFieldProps = Override<
    TextFieldProps,
    {
        min?: number
        max?: number
        value?: number
        defaultValue?: number
        onChange(value: number | undefined, event?: React.SyntheticEvent<HTMLInputElement>): void
        onBlur?(value: number | undefined, event?: React.FocusEvent<HTMLInputElement>): void
        nullable?: boolean
    }
>

type NumberFieldInternalProps = Omit<Override<NumericFormatProps, NumberFieldProps>, "customInput" | "type" | "decimalSeparator">

export const NumberField = forwardRef<HTMLDivElement, NumberFieldInternalProps>((props, ref) => {
    const { decimalSeparator } = useLocalization()
    const { onChange, onBlur, onFocus, isAllowed, min, max, nullable = true, ...restProps } = props

    const handleIsAllowed = useCallback(
        (values: NumberFormatValues) => {
            if (isAllowed?.(values) === false) {
                return false
            }
            const value = values.floatValue
            if (value === undefined) {
                return !!nullable
            }
            return (min === undefined || value >= min) && (max === undefined || value <= max)
        },
        [max, min, nullable, isAllowed]
    )

    const handleValueChange = useCallback<OnValueChange>((values, e) => onChange(values.floatValue, e.event), [onChange])

    const handleBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
        (event) => {
            if (onBlur && !(event.relatedTarget && event.currentTarget?.parentElement?.contains(event.relatedTarget))) {
                // we should return a number as part of this function to avoid parsing string to number everywhere
                onBlur(props.value, event)
            }
        },
        [onBlur, props.value]
    )

    const handleFocus = useCallback<FocusEventHandler<HTMLInputElement>>(
        (event) => {
            event.target.select()
            onFocus?.(event)
        },
        [onFocus]
    )

    return (
        <NumericFormat
            onValueChange={handleValueChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            isAllowed={handleIsAllowed}
            decimalSeparator={decimalSeparator}
            customInput={MuiTextField}
            ref={ref}
            {...restProps}
            defaultValue={undefined}
        />
    )
})
